import { useEffect, useState } from "react";
import { api, type RemediationReport } from "../api/client";
import { Report } from "./RemediationRunPanel";

type HistoryItem = {
  runId: string;
  branch: string | null;
  createdAt: number;
  // Who recorded it, and whether the host attested to it. Without these the
  // list — which is org-wide — renders a forged run identically to a real one.
  createdBy: string | null;
  provenance: "host" | "user" | null;
  // False when the deployment cannot attest at all (no host secret). Badging
  // every row then accuses it of something no operator action can clear.
  attestable?: boolean;
  report: RemediationReport;
};

type RunTone = "clean" | "stable" | "capped" | "failed";

// Human-readable outcome — the old UI showed the raw `capped (setup-failed)`
// + `(no branch)`, which read as a cryptic error wall. Map each outcome to a
// status tone + plain headline, and surface the captured failure reason
// (report.outcome.error) as the subline so a setup failure tells the user what
// to do (e.g. "commit or stash before remediation") instead of "(no branch)".
function runTone(o: RemediationReport["outcome"]): RunTone {
  if (o.cleanAchieved) return "clean";
  if (o.terminationReason === "stable") return "stable";
  if (o.terminationReason === "setup-failed" || o.terminationReason === "run-error")
    return "failed";
  return "capped";
}

function runHeadline(o: RemediationReport["outcome"]): string {
  if (o.cleanAchieved) return "Clean — reached 100";
  if (o.terminationReason === "stable") return "Stable — no further gains";
  if (o.terminationReason === "setup-failed") return "Setup failed";
  if (o.terminationReason === "run-error") return "Run error";
  return `Capped — ${o.terminationReason}`;
}

function clampPct(v: number | null | undefined): number {
  if (v == null) return 0;
  return Math.max(0, Math.min(100, Math.round(v)));
}

// Past "Fix to 100" runs for the org, rendered inline below the live panel.
// Each row is a one-line status summary; clicking expands the SAME structured
// report the live panel shows (the host maps each record through buildReport —
// see src/remediation/reportBuilder.js mapRunsToReports). History is
// non-critical: a load failure shows a quiet note and never blocks the card.
export function RemediationHistory({
  refreshKey,
  onOpenBranch,
}: {
  refreshKey: number;
  onOpenBranch: (branch: string) => void;
}) {
  const [runs, setRuns] = useState<HistoryItem[]>([]);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listRemediationRuns();
        if (!cancelled) {
          // Fall back to [] if a malformed response lacks `runs` — else
          // setRuns(undefined) crashes the card at `runs.length` in render.
          setRuns(res?.runs ?? []);
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (error) {
    return (
      <p className="remediation-note" data-testid="remediation-history-error">
        Couldn't load run history.
      </p>
    );
  }
  if (runs.length === 0) return null;

  return (
    <div className="remediation-history" data-testid="remediation-history">
      <p className="remediation-history-title">Recent runs</p>
      <ul className="remediation-history-list">
        {runs.map((run) => {
          const open = expanded === run.runId;
          const o = run.report.outcome;
          const tone = runTone(o);
          const headline = runHeadline(o);
          const finalHealth = run.report.summary.finalHealth;
          // For failures the captured error IS the actionable reason; otherwise
          // show health + branch as before.
          const reason = tone === "failed" ? o.error : null;
          return (
            <li key={run.runId}>
              <button
                type="button"
                className="rem-run-row remediation-history-row"
                aria-expanded={open}
                onClick={() => setExpanded(open ? null : run.runId)}
              >
                <span className="rem-run-row__top">
                  <span className={`rem-pill rem-pill--${tone}`} data-tone={tone}>
                    {headline}
                  </span>
                  <span className="rem-run-row__date remediation-history-date">
                    {run.createdAt ? new Date(run.createdAt).toLocaleString() : "—"}
                  </span>
                  {/* `provenance === null` means the row predates the two-level
                      trust boundary — and those passed the OLD hard host-secret
                      gate, so they are the most attested rows in the table.
                      Marking them unattested is the inverse of the truth, which
                      is exactly the bug fixed for reviewRuns and then repeated
                      here one table over. Only an explicit "user" is marked. */}
                  {run.attestable !== false && run.provenance === "user" && run.createdBy ? (
                    <span
                      className="rem-run-row__actor"
                      data-testid="remediation-run-unattested"
                      title="Not host-attested — recorded by a signed-in client"
                    >
                      {run.createdBy}
                    </span>
                  ) : null}
                </span>
                {reason ? (
                  <span className="rem-run-row__reason" title={reason}>
                    {reason}
                  </span>
                ) : (
                  <span className="rem-run-row__sub remediation-history-sub">
                    <span className="remediation-history-health">
                      health {finalHealth ?? "—"}
                      <span className="rem-run-row__health-of">/100</span>
                    </span>
                    <span className="remediation-history-branch">
                      {run.branch ? <code>{run.branch}</code> : "no worktree"}
                    </span>
                  </span>
                )}
                {finalHealth != null && (
                  <span className="rem-healthbar" aria-hidden="true">
                    <span
                      className={`rem-healthbar__fill rem-healthbar__fill--${tone}`}
                      style={{ width: `${clampPct(finalHealth)}%` }}
                    />
                  </span>
                )}
              </button>
              {open && <Report report={run.report} onOpenBranch={onOpenBranch} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
